import { Box, Tooltip, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  BaseEdge,
  Edge,
  EdgeProps,
  Handle,
  Node,
  NodeProps,
  Position,
} from "@xyflow/react";
import React from "react";

import { ChartPalette, labelBacking, monospaceStyle } from "../../../theme";
import {
  PartnershipCluster,
  PartnershipNode,
  PartnershipPair,
} from "../../../types/analytics";
import { CountryFlag, flagCode } from "../../common/CountryFlag";
import { counted, ringGuideOf } from "../Ecosystem/EcosystemParts";
import { CLUSTER_LABEL_HEIGHT, LABEL_WIDTH } from "./partnershipsLayout";

// The opacity of a group or a tie the focus does not keep lit, as the
// ecosystem dims them
const DIMMED_OPACITY = 0.25;

// A colour per cluster, by its number: warm and cool hues apart from the
// accent blue, which marks the ties in focus, each light enough to read on
// the dark paper. Past the last the colours come round again. They are the
// theme's `chart.clusters`, and a group in no cluster takes
// `chart.unclustered`.
export const clusterColour = (cluster: number | null, chart: ChartPalette): string =>
  cluster === null ? chart.unclustered : chart.clusters[cluster % chart.clusters.length];

// The ties are grey at rest: lighter inside a cluster, fainter across two,
// and a spoke to the organization in focus between the two. The ties of the
// group in focus take the accent. They are `chart.muted` inside and
// `chart.edge` across.
// How much of a tie shows: inside a cluster, across two, a spoke, lit by
// the focus, out of a lit cluster, and out of the focus
export const TIE_OPACITY = {
  inside: 0.6,
  across: 0.35,
  spoke: 0.7,
  active: 1,
  route: 0.55,
  dimmed: 0.08,
} as const;
// The thinnest and the thickest tie, against the strongest drawn
const TIE_WIDTH_MIN = 0.75;
const TIE_WIDTH_MAX = 5;
// Width (px) of the band along a tie that answers the pointer
const HOVER_WIDTH = 12;

export const tieWidth = (strength: number, most: number): number =>
  most > 0
    ? Math.round((TIE_WIDTH_MIN + ((TIE_WIDTH_MAX - TIE_WIDTH_MIN) * strength) / most) * 100) /
      100
    : TIE_WIDTH_MIN;

// A strength as the view writes it: one decimal, two where the second says
// something (a third of a project)
export const strengthText = (value: number): string =>
  Math.abs(value * 10 - Math.round(value * 10)) < 1e-9 ? value.toFixed(1) : value.toFixed(2);

export const groupName = (node: PartnershipNode): string => node.entity.name ?? node.entity.id;

// What a tie is made of, in a line: its shared projects, its products made
// together, and a stated partnership
export const tieMakeup = (pair: PartnershipPair): string =>
  [
    pair.projects > 0 ? counted(pair.projects, "shared project") : null,
    pair.products > 0
      ? `${counted(pair.products, "product")} made together`
      : null,
    pair.stated ? "partnership stated" : null,
  ]
    .filter(Boolean)
    .join(" · ");

// What a tie says where the pointer rests on it
export const tieReading = (pair: PartnershipPair, a: string, b: string): string =>
  `${a} × ${b}\nstrength ${strengthText(pair.strength)}${
    tieMakeup(pair) ? ` · ${tieMakeup(pair)}` : ""
  }`;

// The mark of a group: a circle tinted in its cluster's colour, laid over
// the paper so that no line shows through it. The organization in focus is
// tinted in the accent and haloed, as the ecosystem's centre. A group of
// several clusters is split into their colours, its own cluster's first.
// A group whose home country is on record is filled with its flag instead,
// and its cluster's colours, split the same way, ring the flag.
export const GroupMark: React.FC<{
  colour: string;
  size: number;
  centre?: boolean;
  selected?: boolean;
  sample?: boolean;
  also?: string[];
  country?: string | null;
}> = ({
  colour,
  size,
  centre = false,
  selected = false,
  sample = false,
  also = [],
  country = null,
}) => {
  const theme = useTheme();
  const paper = theme.palette.background.paper;
  const tint = alpha(colour, centre ? 0.3 : 0.28);
  const colours = [colour, ...also];
  const slice = 360 / colours.length;
  const split = (strength: number) =>
    `conic-gradient(${colours
      .map(
        (each, index) =>
          `${alpha(each, strength)} ${index * slice}deg ${(index + 1) * slice}deg`,
      )
      .join(", ")})`;
  const flagged = country !== null && flagCode(country) !== null;
  // The ring round a flag: wide enough to read its colours beside the flag's
  const ring = centre ? 2.5 : 2;
  const fill = flagged
    ? also.length > 0
      ? split(1)
      : `linear-gradient(${colour}, ${colour})`
    : also.length > 0
      ? split(0.55)
      : `linear-gradient(${tint}, ${tint})`;
  return (
    <Box
      component="span"
      aria-hidden={sample || undefined}
      data-testid={sample ? "pt-sample" : "pt-mark"}
      data-colour={colour}
      data-also={also.length > 0 ? also.join(" ") : undefined}
      sx={{
        flexShrink: 0,
        boxSizing: "border-box",
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        ...(flagged
          ? { p: `${ring}px` }
          : {
              borderWidth: centre ? 2 : 1.5,
              borderStyle: "solid",
              borderColor: centre ? colour : alpha(colour, 0.85),
            }),
        backgroundColor: paper,
        backgroundImage: fill,
        boxShadow: selected
          ? `0 0 0 3px ${paper}, 0 0 0 5px ${theme.palette.text.primary}`
          : centre && !sample
            ? `0 0 0 4px ${alpha(colour, 0.3)}`
            : undefined,
      }}
    >
      {flagged && (
        <CountryFlag
          code={country}
          size={size - 2 * ring}
          round
          sx={{ display: "block", boxShadow: "none" }}
        />
      )}
    </Box>
  );
};

export interface GroupNodeData extends Record<string, unknown> {
  node: PartnershipNode;
  // The diameter of its mark
  size: number;
  colour: string;
  // The colours of the other clusters it belongs to
  also: string[];
  // The organization in focus, at the centre of the partners
  centre: boolean;
  // The small line beneath its name
  caption: string | null;
  selected: boolean;
  dimmed: boolean;
  onSelect: (id: string) => void;
  // The pointer or the keyboard is on the group, or has left it (null)
  onPoint: (id: string | null) => void;
}

export type GroupFlowNode = Node<GroupNodeData, "org">;

// A name longer than this does not fit its two lines, and is given whole
// where the pointer rests on it
const LONG_NAME = 32;

// A group: its mark, and beneath it its name on two lines at the most and
// a line of where it is from. The ties meet at the middle of the mark.
export const GroupNodeView: React.FC<NodeProps<GroupFlowNode>> = ({ data }) => {
  const { node, size, colour, also, centre, caption, selected, dimmed, onSelect, onPoint } = data;
  const theme = useTheme();
  const name = groupName(node);
  const handle: React.CSSProperties = {
    left: "50%",
    top: size / 2,
    opacity: 0,
    pointerEvents: "none",
  };
  return (
    <Box
      role="button"
      tabIndex={0}
      className="nodrag nopan"
      aria-label={name}
      aria-pressed={selected}
      data-testid="pt-group"
      data-cluster={node.cluster ?? ""}
      data-centre={centre ? "true" : "false"}
      data-dimmed={dimmed ? "true" : "false"}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(node.id);
        }
      }}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(node.id);
      }}
      onMouseEnter={() => onPoint(node.id)}
      onMouseLeave={() => onPoint(null)}
      onFocus={() => onPoint(node.id)}
      onBlur={() => onPoint(null)}
      sx={{
        position: "relative",
        width: LABEL_WIDTH,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "3px",
        textAlign: "center",
        cursor: "pointer",
        opacity: dimmed ? DIMMED_OPACITY : 1,
        transition: "opacity 120ms",
        borderRadius: "6px",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "link.main",
          outlineOffset: 3,
        },
      }}
    >
      <Handle type="target" position={Position.Top} isConnectable={false} style={handle} />
      <GroupMark
        colour={centre ? theme.palette.accent.main : colour}
        size={size}
        centre={centre}
        selected={selected}
        also={centre ? [] : also}
        country={node.country}
      />
      <Box
        component="span"
        title={name.length > LONG_NAME ? name : undefined}
        sx={{
          maxWidth: "100%",
          px: 0.5,
          borderRadius: "4px",
          backgroundColor: labelBacking(theme),
          color: "text.primary",
          fontSize: centre ? "12.5px" : "11px",
          fontWeight: centre ? 600 : 500,
          lineHeight: 1.3,
          overflowWrap: "anywhere",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {name}
      </Box>
      {caption && (
        <Box
          component="span"
          sx={{
            ...monospaceStyle,
            px: 0.5,
            borderRadius: "4px",
            backgroundColor: labelBacking(theme),
            color: "text.secondary",
            fontSize: "10px",
            lineHeight: 1.3,
          }}
        >
          {caption}
        </Box>
      )}
      <Handle type="source" position={Position.Bottom} isConnectable={false} style={handle} />
    </Box>
  );
};

export interface ShapeNodeData extends Record<string, unknown> {
  cluster: PartnershipCluster;
  colour: string;
  // Its outline, in the canvas's coordinates, and the box it is drawn in
  path: string;
  box: { left: number; top: number; width: number; height: number };
  // The middle of its name, from the box's left, and the width it wraps
  // within
  labelX: number;
  labelWidth: number;
  // The cluster selected
  lit: boolean;
  // Another cluster selected
  dimmed: boolean;
  onSelect: (cluster: number) => void;
}

export type ShapeFlowNode = Node<ShapeNodeData, "shape">;

// A cluster: a soft outline tinted in its colour round its groups, beneath
// the ties, with its name above it in its colour. Where two clusters share a
// group their outlines overlap round it. The outline and the name select
// it; the box round them lets the pointer through.
export const ShapeNodeView: React.FC<NodeProps<ShapeFlowNode>> = ({ data }) => {
  const { cluster, colour, path, box, labelX, labelWidth, lit, dimmed, onSelect } = data;
  const select = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    onSelect(cluster.id);
  };
  return (
    <Box
      data-testid="pt-cluster"
      data-cluster={cluster.id}
      data-colour={colour}
      data-lit={lit ? "true" : "false"}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        opacity: dimmed ? 0.45 : 1,
        transition: "opacity 120ms",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        className="nodrag nopan"
        aria-label={`Cluster: ${cluster.name}`}
        aria-pressed={lit}
        onClick={select}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            select(event);
          }
        }}
        sx={{
          position: "absolute",
          left: labelX,
          top: 0,
          transform: "translateX(-50%)",
          width: labelWidth,
          height: CLUSTER_LABEL_HEIGHT,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          pb: "4px",
          boxSizing: "border-box",
          pointerEvents: "auto",
          cursor: "pointer",
          "&:focus-visible": { outline: "2px solid", outlineColor: "link.main" },
        }}
      >
        <Box
          component="span"
          data-testid="pt-cluster-name"
          sx={{
            color: colour,
            fontSize: "10.5px",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            lineHeight: 1.25,
            textAlign: "center",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {cluster.name}
        </Box>
      </Box>
      <Box
        component="svg"
        aria-hidden
        viewBox={`${box.left} ${box.top} ${box.width} ${box.height}`}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          overflow: "visible",
          pointerEvents: "none",
        }}
      >
        <Box
          component="path"
          data-testid="pt-cluster-shape"
          d={path}
          onClick={select}
          sx={{
            fill: alpha(colour, lit ? 0.15 : 0.07),
            stroke: alpha(colour, lit ? 0.85 : 0.35),
            strokeWidth: 1.5,
            transition: "fill 120ms, stroke 120ms",
            pointerEvents: "visiblePainted",
            cursor: "pointer",
            "&:hover": { stroke: alpha(colour, 0.6) },
          }}
        />
      </Box>
    </Box>
  );
};

export interface RingGuideData extends Record<string, unknown> {
  ring: number;
}

export type RingGuideFlowNode = Node<RingGuideData, "ring">;

// A solid guide ellipse beneath the partners, as the ecosystem's
export const RingGuideView: React.FC<NodeProps<RingGuideFlowNode>> = ({ data }) => (
  <Box
    role="group"
    aria-label={`Ring ${data.ring}`}
    data-testid="pt-ring"
    sx={{
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      border: (theme) => `1.5px solid ${ringGuideOf(theme.palette.chart)}`,
      boxSizing: "border-box",
      pointerEvents: "none",
    }}
  />
);

export type LooseFlowNode = Node<Record<string, unknown>, "loose">;

// The words over the groups in no cluster
export const LooseLabelView: React.FC<NodeProps<LooseFlowNode>> = () => (
  <Box
    data-testid="pt-loose"
    sx={{
      width: "100%",
      textAlign: "center",
      color: (theme) => theme.palette.chart.unclustered,
      fontSize: "10.5px",
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      pointerEvents: "none",
    }}
  >
    In no cluster
  </Box>
);

export interface TieEdgeData extends Record<string, unknown> {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  pair: PartnershipPair;
  width: number;
  inside: boolean;
  spoke: boolean;
  // What the tie says, where the pointer rests on it
  reading: string;
  // One of its ends is in focus
  active: boolean;
  // It leaves the cluster in focus
  route: boolean;
  dimmed: boolean;
  // Drawn only while lit: a tie between two partners
  hidden: boolean;
  // Selects the tie; none where a tie is not selectable
  onSelect: ((pair: PartnershipPair) => void) | null;
}

export type TieFlowEdge = Edge<TieEdgeData, "tie">;

// A tie between two groups, as wide as it is strong: grey at rest, in the
// accent while one of its ends is in focus, standing back while another is
export const TieLine: React.FC<EdgeProps<TieFlowEdge>> = ({ id, data }) => {
  const theme = useTheme();
  if (!data || (data.hidden && !data.active)) {
    return null;
  }
  const path = `M ${data.x1} ${data.y1} L ${data.x2} ${data.y2}`;
  const lit = data.active || data.route;
  const stroke = lit
    ? theme.palette.accent.main
    : data.inside || data.spoke
      ? theme.palette.chart.muted
      : theme.palette.chart.edge;
  const opacity = data.dimmed
    ? TIE_OPACITY.dimmed
    : data.active
      ? TIE_OPACITY.active
      : data.route
        ? TIE_OPACITY.route
        : data.spoke
          ? TIE_OPACITY.spoke
          : data.inside
            ? TIE_OPACITY.inside
            : TIE_OPACITY.across;
  const select = data.onSelect;
  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        interactionWidth={0}
        style={{
          stroke,
          strokeWidth: data.width,
          strokeLinecap: "round",
          opacity,
          transition: "opacity 120ms",
        }}
      />
      <Tooltip
        title={data.reading}
        followCursor
        enterDelay={150}
        slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
      >
        <path
          d={path}
          fill="none"
          stroke="transparent"
          strokeWidth={HOVER_WIDTH}
          data-testid="pt-tie"
          data-a={data.pair.a}
          data-b={data.pair.b}
          data-inside={data.inside ? "true" : "false"}
          data-spoke={data.spoke ? "true" : "false"}
          data-active={data.active ? "true" : "false"}
          data-route={data.route ? "true" : "false"}
          data-dimmed={data.dimmed ? "true" : "false"}
          data-stroke={stroke}
          data-width={data.width}
          style={{ cursor: select ? "pointer" : undefined }}
          onClick={
            select
              ? (event) => {
                  event.stopPropagation();
                  select(data.pair);
                }
              : undefined
          }
        />
      </Tooltip>
    </>
  );
};

// A short stretch of tie, as the canvas draws it, `lit` in the accent
export const TieSample: React.FC<{ width?: number; lit?: boolean; inside?: boolean }> = ({
  width = 1.5,
  lit = false,
  inside = true,
}) => {
  const theme = useTheme();
  return (
    <Box component="svg" width={26} height={8} aria-hidden sx={{ flexShrink: 0 }}>
      <line
        x1={1}
        y1={4}
        x2={25}
        y2={4}
        stroke={
          lit
            ? theme.palette.accent.main
            : inside
              ? theme.palette.chart.muted
              : theme.palette.chart.edge
        }
        strokeWidth={width}
        strokeLinecap="round"
      />
    </Box>
  );
};

// A small tinted ellipse, as a cluster is drawn
export const ShapeSample: React.FC<{ colour: string }> = ({ colour }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      flexShrink: 0,
      width: 26,
      height: 14,
      borderRadius: "50%",
      boxSizing: "border-box",
      border: `1.5px solid ${alpha(colour, 0.5)}`,
      backgroundColor: alpha(colour, 0.12),
    }}
  />
);
